import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { UseFormReturn, FieldValues, Path } from 'react-hook-form';

/**
 * Hook to automatically prefill form fields from URL search parameters.
 * Matches search param keys to form field names and sets the values.
 *
 * Security: useSearchParams() from Next.js automatically decodes URL parameters
 * and returns them as plain strings. React Hook Form and React will escape any
 * values when rendering, preventing XSS attacks.
 *
 * @param form - React Hook Form instance
 * @param fields - Optional array of field names to prefill. If not provided, all search params will be attempted.
 *
 * @example
 * const form = useForm<LoginFormValues>({ ... });
 * useFormPrefill(form, ['email']); // Only prefill email field
 * // URL: /login?email=user@example.com -> email field will be pre-filled
 */
export function useFormPrefill<TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  fields?: (keyof TFieldValues)[]
) {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!searchParams) return;

    // Get all search param entries - values are already decoded by useSearchParams
    const params = Array.from(searchParams.entries());
    console.log('[useFormPrefill] URL search params:', params);

    params.forEach(([key, value]) => {
      // If fields array is provided, only prefill those fields
      if (fields && !fields.includes(key as keyof TFieldValues)) {
        console.log(`[useFormPrefill] Skipping field '${key}' - not in allowed list:`, fields);
        return;
      }

      // Check if this field exists in the form
      const fieldName = key as Path<TFieldValues>;

      try {
        // Only set the value if the field is currently empty
        const currentValue = form.getValues(fieldName);
        if (!currentValue) {
          console.log(`[useFormPrefill] Setting field '${key}' to:`, value);
          // Set value as plain text - React Hook Form will handle it safely
          form.setValue(fieldName, value as any, {
            shouldValidate: false,
            shouldDirty: false,
            shouldTouch: false
          });
        } else {
          console.log(`[useFormPrefill] Field '${key}' already has value:`, currentValue);
        }
      } catch (error) {
        // Field doesn't exist in form, skip it
        console.debug(`Field '${key}' not found in form, skipping prefill`);
      }
    });
  }, [searchParams, form, fields]);
}

/**
 * Helper function to create a URL with query parameters for form prefilling.
 *
 * Security: URLSearchParams.set() automatically encodes values properly,
 * preventing URL injection attacks. Values are treated as plain text.
 *
 * @param path - The target path
 * @param params - Object containing field names and values to pass as query params
 * @returns URL string with properly encoded query parameters
 *
 * @example
 * createFormLink('/register', { email: 'user@example.com' })
 * // Returns: '/register?email=user%40example.com'
 */
export function createFormLink(
  path: string,
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      // URLSearchParams.set() automatically encodes the value safely
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  const result = queryString ? `${path}?${queryString}` : path;
  console.log('[createFormLink] Generated link:', result, 'from params:', params);
  return result;
}
